import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Nav, NavItem, NavLink, TabContent, TabPane, Button, Badge, Table } from 'reactstrap';
import classnames from 'classnames';
import moment from 'moment';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import {
  getUserWorkspaces,
  getUserBillingHistory,
  getUserSecurityLogs,
  getUserActivities
} from '../../../store/Admin';
import './UserSidebar.css';
import notFoundImg from '../../../static/img/not-found.png';

const UserSidebar = ({ isOpen, onClose, user }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState({});

  const {
    userWorkspaces = [],
    userBillingHistory = {},
    userSecurityLogs = [],
    activityLogs = [],
    loadingUserWorkspaces,
    loadingUserBilling,
    loadingUserSecurity,
    loadingUserActivities
  } = useSelector((state) => state.Admin);

  useEffect(() => {
    if (user && user._id && isOpen) {
      loadTabData(activeTab);
    }
  }, [user, isOpen]);

  useEffect(() => {
    if (user && user._id && isOpen) {
      loadTabData(activeTab);
    }
  }, [activeTab]);

  const loadTabData = (tab) => {
    if (!user || !user._id) return;

    switch (tab) {
      case 'activities':
        dispatch(getUserActivities({ userId: user._id, limit: 20 }));
        break;
      case 'workspaces':
        dispatch(getUserWorkspaces({ userId: user._id }));
        break;
      case 'billing':
        dispatch(getUserBillingHistory({ userId: user._id }));
        break;
      case 'security':
        dispatch(getUserSecurityLogs({ userId: user._id, limit: 15 }));
        break;
      default:
        break;
    }
  };

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  // Helper functions for activity display
  const getActivityIcon = (type) => {
    // Map activity types to icons
    const typeIcons = {
      'authentication': 'fa-lock',
      'contact': 'fa-address-book',
      'deal': 'fa-handshake-o',
      'email': 'fa-envelope',
      'call': 'fa-phone',
      'agent': 'fa-robot',
      'workspace': 'fa-building',
      'api': 'fa-code',
      'settings': 'fa-cog',
      'system': 'fa-server'
    };

    // Map original actions to specific icons - Enhanced with more actions
    const actionIcons = {
      // User Management Actions
      'user_created': 'fa-user-plus',
      'user_updated': 'fa-user-edit',
      'user_deleted': 'fa-user-times',
      'user_activated': 'fa-user-check',
      'user_deactivated': 'fa-user-slash',
      'user_suspended': 'fa-user-lock',
      'user_restored': 'fa-user-shield',
      'user_invited': 'fa-envelope',
      'user_role_changed': 'fa-user-tag',
      'user_password_reset': 'fa-key',
      'user_email_verified': 'fa-envelope-open',
      'user_profile_updated': 'fa-id-card',
      'user_settings_changed': 'fa-user-cog',

      // Admin Actions on Users
      'admin_created_user': 'fa-user-plus',
      'admin_activated_user': 'fa-toggle-on',
      'admin_deactivated_user': 'fa-toggle-off',
      'admin_deleted_user': 'fa-trash',
      'admin_updated_user': 'fa-edit',
      'admin_viewed_user': 'fa-eye',
      'admin_impersonated_user': 'fa-user-secret',
      'admin_exported_users': 'fa-download',

      // Workspace Actions
      'workspace_created': 'fa-building',
      'workspace_updated': 'fa-edit',
      'workspace_suspended': 'fa-pause-circle',
      'workspace_deleted': 'fa-trash-alt',
      'workspace_activated': 'fa-play-circle',
      'workspace_member_added': 'fa-user-plus',
      'workspace_member_removed': 'fa-user-minus',
      'workspace_settings_updated': 'fa-cogs',
      'workspace_plan_changed': 'fa-exchange-alt',

      // Agent Actions
      'agent_created': 'fa-robot',
      'agent_started': 'fa-play-circle',
      'agent_stopped': 'fa-stop-circle',
      'agent_deleted': 'fa-trash-alt',
      'agent_updated': 'fa-edit',
      'agent_configured': 'fa-sliders-h',
      'agent_error': 'fa-exclamation-triangle',
      'agent_completed': 'fa-check-circle',

      // System Actions
      'system_backup': 'fa-database',
      'system_update': 'fa-sync-alt',
      'system_error': 'fa-exclamation-triangle',
      'system_maintenance': 'fa-tools',
      'system_restart': 'fa-redo',

      // Authentication Actions
      'login_success': 'fa-sign-in-alt',
      'login_failed': 'fa-times-circle',
      'logout': 'fa-sign-out-alt',
      'password_changed': 'fa-lock',
      'password_reset_requested': 'fa-envelope',
      'two_factor_enabled': 'fa-shield-alt',
      'two_factor_disabled': 'fa-shield-alt',
      'session_expired': 'fa-clock',

      // Billing/Subscription Actions
      'subscription_created': 'fa-credit-card',
      'subscription_upgraded': 'fa-arrow-up',
      'subscription_downgraded': 'fa-arrow-down',
      'subscription_cancelled': 'fa-times',
      'subscription_renewed': 'fa-sync',
      'payment_success': 'fa-dollar-sign',
      'payment_failed': 'fa-exclamation-circle',
      'refund_processed': 'fa-undo',
      'credit_added': 'fa-plus-circle',

      // Security Actions
      'security_alert': 'fa-shield-alt',
      'api_key_created': 'fa-key',
      'api_key_revoked': 'fa-ban',
      'ip_whitelisted': 'fa-check-circle',
      'ip_blocked': 'fa-ban',
      'suspicious_activity': 'fa-exclamation-triangle',

      // Data Actions
      'data_exported': 'fa-download',
      'data_imported': 'fa-upload',
      'data_deleted': 'fa-trash',
      'data_archived': 'fa-archive',
      'backup_created': 'fa-save',
      'backup_restored': 'fa-undo'
    };

    // First check if it's an activity type, then check action icons
    return typeIcons[type] || actionIcons[type] || 'fa-info-circle';
  };

  const getActivityColor = (severity) => {
    const colors = {
      'success': 'text-success',
      'info': 'text-info',
      'warning': 'text-warning',
      'error': 'text-danger',
      'critical': 'text-danger'
    };
    return colors[severity] || 'text-secondary';
  };

  const getRelativeTime = (timestamp) => {
    const diffMs = Date.now() - new Date(timestamp).getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    return `${day}d ago`;
  };

  const getUserStatusBadge = (status) => {
    const statusColors = { 'active': 'success', 'inactive': 'danger', 'pending': 'warning' };
    return <Badge color={statusColors[status] || 'secondary'}>{status}</Badge>;
  };

  const getRoleBadge = (role) => {
    const roleColors = { 'admin': 'danger', 'user': 'primary' };
    return <Badge color={roleColors[role] || 'secondary'}>{role}</Badge>;
  };

  const renderOverviewTab = () => (
    <div className="tab-content-wrapper">
      <div className="user-overview-section">
        <div className="user-stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fa fa-calendar text-info"></i>
            </div>
            <div className="stat-content">
              <label>Member Since</label>
              <span className='text-info'>{user?.createdAt ? moment(user.createdAt).format('MMM DD, YYYY') : 'N/A'}</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fa fa-clock text-warning"></i>
            </div>
            <div className="stat-content">
              <label>Last Login</label>
              <span className='text-warning'>{user?.lastLogin ? moment(user.lastLogin).fromNow() : 'Never'}</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fa fa-building text-primary"></i>
            </div>
            <div className="stat-content">
              <label>Workspaces</label>
              <span className='text-primary'>{user?.workspaces?.length || 0}</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fa fa-globe text-success"></i>
            </div>
            <div className="stat-content">
              <label>Last IP</label>
              <span className='text-success'>{user?.lastIp || 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className="user-details-section">
          <h6 className="section-title">Account Information</h6>
          <div className="detail-row">
            <span className="detail-label">Email Verified:</span>
            <Badge color={user?.emailVerified ? 'success' : 'warning'}>
              {user?.emailVerified ? 'Verified' : 'Unverified'}
            </Badge>
          </div>
          <div className="detail-row">
            <span className="detail-label">SMS Enabled:</span>
            <Badge color={user?.smsEnabled ? 'info' : 'secondary'}>
              {user?.smsEnabled ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
          <div className="detail-row">
            <span className="detail-label">Provider:</span>
            <Badge color="light" className="text-dark text-capitalize">
              {user?.provider || 'Local'}
            </Badge>
          </div>
          <div className="detail-row">
            <span className="detail-label">Timezone:</span>
            <span>{user?.timezone || 'GMT'}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderActivitiesTab = () => {
    // Function to format activity description based on action type
    const formatActivityDescription = (activity) => {
      const actor = activity.actor?.name || activity.actor?.email || 'User';
      const action = activity.originalAction || activity.action || activity.type || '';

      // Map actions to human-readable descriptions
      const actionDescriptions = {
        // Admin actions on users
        'admin_created_user': 'created a new user',
        'admin_activated_user': 'activated user',
        'admin_deactivated_user': 'deactivated user',
        'admin_deleted_user': 'deleted user',
        'admin_updated_user': 'updated user details',
        'admin_viewed_user': 'viewed user details',
        'admin_impersonated_user': 'impersonated user',
        'admin_exported_users': 'exported user data',

        // User actions
        'user_created': 'account was created',
        'user_updated': 'profile was updated',
        'user_activated': 'account was activated',
        'user_deactivated': 'account was deactivated',
        'user_deleted': 'account was deleted',
        'user_suspended': 'account was suspended',
        'user_password_reset': 'password was reset',
        'user_email_verified': 'email was verified',
        'user_settings_changed': 'settings were changed',

        // Authentication
        'login_success': 'logged in successfully',
        'login_failed': 'failed login attempt',
        'logout': 'logged out',
        'password_changed': 'changed password',
        'two_factor_enabled': 'enabled two-factor authentication',
        'two_factor_disabled': 'disabled two-factor authentication',
        'session_expired': 'session expired',

        // Workspace actions
        'workspace_created': 'created workspace',
        'workspace_updated': 'updated workspace',
        'workspace_deleted': 'deleted workspace',
        'workspace_suspended': 'suspended workspace',
        'workspace_activated': 'activated workspace',
        'workspace_member_added': 'added member to workspace',
        'workspace_member_removed': 'removed member from workspace',

        // Agent actions
        'agent_created': 'created agent',
        'agent_started': 'started agent',
        'agent_stopped': 'stopped agent',
        'agent_deleted': 'deleted agent',
        'agent_configured': 'configured agent settings',

        // Default for API and other actions
        'api': 'made API request',
        'viewed': 'viewed admin panel'
      };

      // Get the description or fallback to formatting the action
      let description = actionDescriptions[action];
      if (!description) {
        if (activity.details && activity.details !== 'api') {
          description = activity.details;
        } else if (action && action !== 'api') {
          description = action.replace(/_/g, ' ').toLowerCase();
        } else {
          description = 'performed an action';
        }
      }

      return { actor, description };
    };

    return (
      <div className="tab-content-wrapper">
        {loadingUserActivities ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="activity-timeline">
            {(activityLogs && activityLogs.length > 0 ? activityLogs : []).map(activity => {
              const { actor, description } = formatActivityDescription(activity);
              const hasMetadata = activity.metadata && Object.keys(activity.metadata).length > 0;
              const showLocation = activity.metadata?.location || activity.metadata?.ip;
              const actionType = activity.originalAction || activity.type || activity.action;

              return (
                <div key={activity._id} className="activity-item">
                  <div className="activity-icon">
                    <i className={`fa ${getActivityIcon(actionType)} ${getActivityColor(activity.severity || 'info')}`}></i>
                  </div>
                  <div className="activity-content">
                    <div className="activity-main">
                      <p className="mb-1">
                        <strong>{actor}</strong> {description}
                        {activity.workspace && (
                          <span>&nbsp;in <strong>{activity.workspace.name}</strong></span>
                        )}
                      </p>

                      {/* Show target resource if available */}
                      {(activity.target?.name || activity.targetUser) && (
                        <p className="mb-1 text-muted small">
                          <i className="fa fa-crosshairs mr-1"></i>
                          Target: <strong>{activity.target?.name || activity.targetUser}</strong>
                        </p>
                      )}

                      {/* Show result/status if available */}
                      {activity.result && (
                        <p className="mb-1 text-muted small">
                          <i className="fa fa-check-circle mr-1"></i>
                          Result: <strong>{activity.result}</strong>
                        </p>
                      )}

                      {/* Show location/IP if available */}
                      {showLocation && (
                        <p className="mb-1 text-muted small">
                          <i className="fa fa-map-marker mr-1"></i>
                          {activity.metadata?.location?.city && activity.metadata?.location?.country ? (
                            <span>{activity.metadata.location.city}, {activity.metadata.location.country}</span>
                          ) : (
                            <span>IP: {activity.metadata?.ip || activity.ip || 'Unknown'}</span>
                          )}
                        </p>
                      )}

                      {/* Show device/browser info */}
                      {(activity.metadata?.browser || activity.metadata?.os) && (
                        <p className="mb-1 text-muted small">
                          <i className="fa fa-desktop mr-1"></i>
                          {activity.metadata.browser} {activity.metadata.os && `on ${activity.metadata.os}`}
                        </p>
                      )}

                      {/* Show any additional details */}
                      {activity.metadata?.details && (
                        <p className="mb-1 text-muted small">
                          <i className="fa fa-info-circle mr-1"></i>
                          {activity.metadata.details}
                        </p>
                      )}
                    </div>

                    <div className="activity-meta d-flex align-items-center">
                      <small className="text-muted">
                        <i className="fa fa-clock mr-1"></i>
                        {getRelativeTime(activity.timestamp || activity.createdAt)}
                      </small>

                      {/* Activity severity/status badge */}
                      <Badge
                        color={
                          activity.severity === 'error' || activity.success === false ? 'danger' :
                            activity.severity === 'warning' ? 'warning' :
                              activity.severity === 'success' || activity.success === true ? 'success' :
                                'info'
                        }
                        className="ml-2 badge-sm"
                      >
                        {activity.severity || (activity.success === false ? 'failed' : 'success')}
                      </Badge>

                      {/* Activity category badge */}
                      {activity.category && (
                        <Badge color="light" className="ml-2 badge-sm text-dark">
                          {activity.category}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {(!activityLogs || activityLogs.length === 0) && (
              <div className="text-center text-muted mt-4">
                <i className="fa fa-clock fa-2x mb-2 d-block"></i>
                <p>No recent activity found</p>
                <small>Activities will appear here when the user performs actions</small>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderWorkspacesTab = () => (
    <div className="tab-content-wrapper content-dashboard">
      <div className="table-container filters-table">
        {/* Users Table */}
        <div className='table-area'>
          {loadingUserWorkspaces ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Workspace</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Last Accessed</th>
                </tr>
              </thead>
              <tbody>
                {userWorkspaces.length > 0 ? (
                  userWorkspaces.map((membership, index) => (
                    <tr key={index}>
                      <td>
                        <div>
                          <strong>{membership.workspaceId?.name || 'N/A'}</strong>
                          <br />
                          <small className="text-muted">ID: {membership.workspaceId?.id || 'N/A'}</small>
                        </div>
                      </td>
                      <td>
                        <Badge color={
                          membership.role === 'owner' ? 'danger' :
                            membership.role === 'admin' ? 'warning' :
                              'info'
                        }>
                          {membership.role || 'member'}
                        </Badge>
                      </td>
                      <td>
                        <Badge color={membership.status === 'active' ? 'success' : 'secondary'}>
                          {membership.status || 'pending'}
                        </Badge>
                      </td>
                      <td>{moment(membership.createdAt).format('MMM DD, YYYY')}</td>
                      <td>{membership.lastAccessed ? moment(membership.lastAccessed).fromNow() : 'Never'}</td>
                    </tr>
                  ))
                ) : (
                  <div className='notfound-text'>
                    <div className='inner-notfound'>
                      <div className='img-box'>
                        <img alt="..." src={notFoundImg} />
                      </div>
                      <p className="text-center text-muted">No workspace memberships found</p>
                    </div>
                  </div>
                )}
              </tbody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );

  const renderBillingTab = () => {
    const billingData = userBillingHistory;

    return (
      <div className="tab-content-wrapper">
        {loadingUserBilling ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className="counter-sections billing-tab billing-sidebar">
              <div className='counter-box style-two'>
                <div className='counter-start' >
                  <div className='left-text-area'>
                    <h5>Total Spent</h5>
                    <div className='CountUp-box'>
                      <h3 className="" style={{ color: '#2563EB' }}>${billingData?.totalSpent || 0}</h3>

                    </div>
                    <div className="status-text">
                      <small>All time</small>
                    </div>
                  </div>
                  <div className='right-icon'>
                    <span className='icon text-success'>
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle opacity="0.1" cx="16" cy="16" r="16" fill="#2563EB" />
                        <path d="M16.9334 19.6133H15.2601C14.1668 19.6133 13.2801 18.6933 13.2801 17.56C13.2801 17.2867 13.5068 17.06 13.7801 17.06C14.0534 17.06 14.2801 17.2867 14.2801 17.56C14.2801 18.14 14.7201 18.6133 15.2601 18.6133H16.9334C17.3668 18.6133 17.7268 18.2267 17.7268 17.76C17.7268 17.18 17.5201 17.0667 17.1801 16.9467L14.4934 16C13.9734 15.82 13.2734 15.4333 13.2734 14.24C13.2734 13.2133 14.0801 12.3867 15.0668 12.3867H16.7401C17.8334 12.3867 18.7201 13.3067 18.7201 14.44C18.7201 14.7133 18.4934 14.94 18.2201 14.94C17.9468 14.94 17.7201 14.7133 17.7201 14.44C17.7201 13.86 17.2801 13.3867 16.7401 13.3867H15.0668C14.6334 13.3867 14.2734 13.7733 14.2734 14.24C14.2734 14.82 14.4801 14.9333 14.8201 15.0533L17.5068 16C18.0268 16.18 18.7268 16.5667 18.7268 17.76C18.7201 18.78 17.9201 19.6133 16.9334 19.6133Z" fill="#2563EB" />
                        <path d="M16 20.5C15.7267 20.5 15.5 20.2733 15.5 20V12C15.5 11.7267 15.7267 11.5 16 11.5C16.2733 11.5 16.5 11.7267 16.5 12V20C16.5 20.2733 16.2733 20.5 16 20.5Z" fill="#2563EB" />
                        <path d="M16.0002 23.1667C12.0468 23.1667 8.8335 19.9533 8.8335 16C8.8335 12.0467 12.0468 8.83333 16.0002 8.83333C19.9535 8.83333 23.1668 12.0467 23.1668 16C23.1668 19.9533 19.9535 23.1667 16.0002 23.1667ZM16.0002 9.83333C12.6002 9.83333 9.8335 12.6 9.8335 16C9.8335 19.4 12.6002 22.1667 16.0002 22.1667C19.4002 22.1667 22.1668 19.4 22.1668 16C22.1668 12.6 19.4002 9.83333 16.0002 9.83333Z" fill="#2563EB" />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>

              <div className='counter-box style-two'>
                <div className='counter-start' >
                  <div className='left-text-area'>
                    <h5>Active Subscriptions</h5>
                    <div className='CountUp-box'>
                      <h3 className="" style={{ color: '#16A34A' }}>{billingData?.activeSubscriptions || 0}</h3>
                    </div>
                    <div className="status-text">
                      <small>Across workspaces</small>
                    </div>
                  </div>
                  <div className='right-icon'>
                    <span className='icon text-success'>
                      <i className="fa fa-credit-card"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {billingData?.payments && billingData.payments.length > 0 && (
              <>
                <h6 className="mb-3">Payment History</h6>
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th>Workspace</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingData.payments.map((payment, index) => (
                      <tr key={index}>
                        <td>{moment(payment.createdAt).format('MMM DD, YYYY')}</td>
                        <td><strong>${payment.amount}</strong></td>
                        <td>{payment.description || 'N/A'}</td>
                        <td>
                          <Badge color={
                            payment.status === 'succeeded' ? 'success' :
                              payment.status === 'pending' ? 'warning' : 'danger'
                          }>
                            {payment.status}
                          </Badge>
                        </td>
                        <td>{payment.workspace?.name || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}

            {(!billingData?.payments || billingData.payments.length === 0) && (
              <div className="text-center text-muted py-4 d-block mt-5">
                <i className="fa fa-credit-card fa-2x mb-2 d-block"></i>
                <p className="d-block w-100">No billing history found</p>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderSecurityTab = () => (
    <div className="tab-content-wrapper">
      {loadingUserSecurity ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="counter-sections billing-tab security-tab mb-4">
            <div className='counter-box style-two'>
              <div className='counter-start' >
                <div className='left-text-area'>
                  <h5>Security Score</h5>
                  <div className='CountUp-box'>
                    <h3 className="" style={{ color: '#16A34A' }}>85%</h3>
                  </div>
                  <div className="status-text">
                    <small>Good</small>
                  </div>
                </div>
                <div className='right-icon'>
                  <span className='icon text-success'>
                    <i className="fa fa-shield"></i>
                  </span>
                </div>
              </div>
            </div>
            <div className='counter-box style-two'>
              <div className='counter-start' >
                <div className='left-text-area'>
                  <h5>Login Attempts</h5>
                  <div className='CountUp-box'>
                    <h3 className="" style={{ color: '#DF9524' }}>{userSecurityLogs?.filter(log => log.action?.includes('login')).length || 0}</h3>

                  </div>
                  <div className="status-text">
                    <small>Last 30 days</small>
                  </div>
                </div>
                <div className='right-icon'>
                  <span className='icon text-warning'>
                    <i className="fa fa-sign-in-alt"></i>
                  </span>
                </div>
              </div>
            </div>

            <div className='counter-box style-two'>
              <div className='counter-start' >
                <div className='left-text-area'>
                  <h5>Unique IPs</h5>
                  <div className='CountUp-box'>
                    <h3 className="" style={{ color: '#2563EB' }}>{new Set(userSecurityLogs?.map(log => log.metadata?.location?.ip).filter(Boolean)).size || 0}</h3>

                  </div>
                  <div className="status-text">
                    <small>Last 30 days</small>
                  </div>
                </div>
                <div className='right-icon'>
                  <span className='icon text-success'>
                    <i className="fa fa-globe"></i>
                  </span>
                </div>
              </div>
            </div>


          </div>

          <h6 className="mb-3">Security Events</h6>
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                <th>Event</th>
                <th>IP Address</th>
                <th>Location</th>
                <th>Device</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {userSecurityLogs && userSecurityLogs.length > 0 ? (
                userSecurityLogs.map((log, index) => (
                  <tr key={index}>
                    <td>
                      <div className="d-flex align-items-center">
                        <i className={`fa ${getActivityIcon(log.action)} mr-2 ${getActivityColor(log.severity)}`}></i>
                        <span>{log.action?.replace(/_/g, ' ')}</span>
                      </div>
                    </td>
                    <td>{log.metadata?.location?.ip || log.ip || 'N/A'}</td>
                    <td>
                      {log.metadata?.location?.city && log.metadata?.location?.country ?
                        `${log.metadata.location.city}, ${log.metadata.location.country}` :
                        'Unknown'
                      }
                    </td>
                    <td>
                      <div>
                        {log.metadata?.browser && <small>{log.metadata.browser}</small>}
                        {log.metadata?.os && <><br /><small className="text-muted">{log.metadata.os}</small></>}
                      </div>
                    </td>
                    <td>{moment(log.createdAt).format('MMM DD, HH:mm')}</td>
                  </tr>
                ))
              ) : (
                <div className='notfound-text'>
                  <div className='inner-notfound'>
                    <div className='img-box'>
                      <img alt="..." src={notFoundImg} />
                    </div>
                    <p className="text-center text-muted">No security events found</p>
                  </div>
                </div>
              )}
            </tbody>
          </Table>
        </>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <>
      {/* <div >
        <div className="sidebar-header">
          <Button
            color="link"
            className="close-btn"
            onClick={onClose}
          >
            <i className="fa fa-times"></i>
          </Button>

          <div className="user-info">
            <div className="user-title-section">
              <h3 className="user-name">
                <i className="fa fa-user-circle-o mr-2 text-primary"></i>
                {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || 'User Details'}
              </h3>
              <div className="user-badges">
                {getRoleBadge(user?.role)}
                {getUserStatusBadge(user?.status)}
              </div>
            </div>

            <div className="user-meta-info">
              <div className="meta-item">
                <i className="fa fa-envelope-o mr-2"></i>
                <span>{user?.email || 'N/A'}</span>
              </div>
              {user?.phone && (
                <div className="meta-item">
                  <i className="fa fa-phone mr-2"></i>
                  <span>{user.phone}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sidebar-body">
          <Nav tabs className="user-tabs">
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === 'overview' })}
                onClick={() => toggle('overview')}
              >
                <i className="fa fa-user mr-1"></i>
                Overview
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === 'activities' })}
                onClick={() => toggle('activities')}
              >
                <i className="fa fa-clock-o mr-1"></i>
                Activities
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === 'workspaces' })}
                onClick={() => toggle('workspaces')}
              >
                <i className="fa fa-building mr-1"></i>
                Workspaces
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === 'billing' })}
                onClick={() => toggle('billing')}
              >
                <i className="fa fa-credit-card mr-1"></i>
                Billing
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === 'security' })}
                onClick={() => toggle('security')}
              >
                <i className="fa fa-shield mr-1"></i>
                Security
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="overview">
              {renderOverviewTab()}
            </TabPane>
            <TabPane tabId="activities">
              {renderActivitiesTab()}
            </TabPane>
            <TabPane tabId="workspaces">
              {renderWorkspacesTab()}
            </TabPane>
            <TabPane tabId="billing">
              {renderBillingTab()}
            </TabPane>
            <TabPane tabId="security">
              {renderSecurityTab()}
            </TabPane>
          </TabContent>
        </div>
      </div> */}
      <Modal isOpen={isOpen} modalClassName='right right-without-bg' backdrop={false} className={`main-modal right right-side-modal style-three`}>
        <ModalHeader toggle={onClose}>
          <h3>User Detail</h3>
        </ModalHeader>
        <ModalBody>
          <div className='filters-modal-content'>
            <div className='function-box mb-4'>
              <div className="user-info">
                <div className="user-title-section">
                  <h3 className="user-name">
                    <i className="icon">
                      <svg width="800px" height="800px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 21C5 17.134 8.13401 14 12 14C15.866 14 19 17.134 19 21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                    </i>
                    {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || 'User Details'}
                  </h3>
                  <div className="user-badges">
                    {getRoleBadge(user?.role)}
                    {getUserStatusBadge(user?.status)}
                  </div>
                </div>

                <div className="user-meta-info">
                  <div className="meta-item">
                    <i className="icon">
                      <svg fill="currentColor" height="800px" width="800px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" enable-background="new 0 0 64 64">
                        <path id="Mail" d="M58.0034485,8H5.9965506c-3.3136795,0-5.9999995,2.6862001-5.9999995,6v36c0,3.3137016,2.6863203,6,5.9999995,6
                                h52.006897c3.3137016,0,6-2.6862984,6-6V14C64.0034485,10.6862001,61.3171501,8,58.0034485,8z M62.0034485,49.1108017
                                L43.084549,30.1919994l18.9188995-12.0555992V49.1108017z M5.9965506,10h52.006897c2.2056007,0,4,1.7943001,4,4v1.7664003
                                L34.4677505,33.3134003c-1.4902,0.9492989-3.3935013,0.9199982-4.8495998-0.0703011L1.9965508,14.4694996V14
                                C1.9965508,11.7943001,3.7910507,10,5.9965506,10z M1.9965508,16.8852005L21.182251,29.9251003L1.9965508,49.1108017V16.8852005z
                                M58.0034485,54H5.9965506c-1.6473999,0-3.0638998-1.0021019-3.6760998-2.4278984l20.5199013-20.5200024l5.6547985,3.843401
                                c1.0859013,0.7383003,2.3418007,1.1083984,3.5995998,1.1083984c1.1953011,0,2.3925018-0.3339996,3.4463005-1.0048981
                                l5.8423996-3.7230015l20.2961006,20.2961025C61.0673485,52.9978981,59.6508713,54,58.0034485,54z"/>
                      </svg>
                    </i>
                    <span>{user?.email || 'N/A'}</span>
                  </div>
                  {user?.phone && (
                    <div className="meta-item">
                      <i className="fa fa-phone mr-2"></i>
                      <span>{user.phone}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="sidebar-body">
                <div className='tabs-area'>
                  {/* Navigation Tabs */}
                  <div className='tab-header'>
                    {/* Navigation Tabs */}
                    <Nav tabs className="mb-2 mt-4">
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'overview' })}
                          onClick={() => toggle('overview')}
                        >
                          <i className="fa fa-user mr-1"></i>
                          Overview
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'activities' })}
                          onClick={() => toggle('activities')}
                        >
                          <i className="fa fa-clock-o mr-1"></i>
                          Activities
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'workspaces' })}
                          onClick={() => toggle('workspaces')}
                        >
                          <i className="fa fa-building mr-1"></i>
                          Workspaces
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'billing' })}
                          onClick={() => toggle('billing')}
                        >
                          <i className="fa fa-credit-card mr-1"></i>
                          Billing
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'security' })}
                          onClick={() => toggle('security')}
                        >
                          <i className="fa fa-shield mr-1"></i>
                          Security
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>
                </div>

                <TabContent activeTab={activeTab}>
                  <TabPane tabId="overview">
                    {renderOverviewTab()}
                  </TabPane>
                  <TabPane tabId="activities">
                    {renderActivitiesTab()}
                  </TabPane>
                  <TabPane tabId="workspaces">
                    {renderWorkspacesTab()}
                  </TabPane>
                  <TabPane tabId="billing">
                    {renderBillingTab()}
                  </TabPane>
                  <TabPane tabId="security">
                    {renderSecurityTab()}
                  </TabPane>
                </TabContent>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default UserSidebar;